<script>
export default {
    // The show lives in the store, so page() can read it straight from there — no need for the
    // options-data bridge the chart and genre views use for their locally-fetched entities.
    page() {
        const show = this.$store.state.shows.activeObject
        const title = show && show.title ? show.title : 'Show'
        const genre = show && show.genre && show.genre.length ? show.genre[0] : ''
        const description = (show && show.summary)
            || `Listen to ${title} on FRISKY${genre ? ' • ' + genre : ''}`
        const image = (show && show.album_art && show.album_art.url)
            || `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'music.song' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import DataLayer from '@services/data-layer/index'
import ShowService from './../../services/show-service'
import Layout from '@layouts/main-layout'
import Overview from '@components/show/overview'
import About from '@components/show/about'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes name the tab, so the name is mapped back here.
const MAIN_COMPONENTS = { Overview, About }

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'Overview',
    },
})

const route = useRoute()
const store = useStore()

const latestMixes = ref([])
const popularMixes = ref([])
const artists = ref([])

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const show = computed(() => store.state.shows.activeObject)

const tabs = computed(() => {
    const id = route.params.id
    return [
        { to: { name: 'show', params: { id } }, label: 'Overview' },
        { to: { name: 'show/about', params: { id } }, label: 'About' },
    ]
})

function checkForAdditionalReach(id) {
    if (ShowService().hasAdditionalReach(id)) store.dispatch('shows/increaseReach')
}

onMounted(() => {
    const id = route.params.id

    Promise.all([
        DataLayer.shows.getById(id),
        DataLayer.mixes.getPopularByShow(id),
        DataLayer.mixes.getLatestByShow(id),
    ]).then(([showResponse, popular, latest]) => {
        store.commit('shows/SET_ACTIVE', showResponse.body)
        artists.value = [showResponse.body.artist]
        popularMixes.value = popular.body
        latestMixes.value = latest.body
        checkForAdditionalReach(id)
    })
})
</script>

<template>
    <Layout>
        <BaseHero
            :resource="show"
            :mix="latestMixes[0]"
            resource-type="show"
        />
        <BaseTabPanel
            :tabs="tabs"
            class="tabs"
            is-unpadded
            address-navigation
        />
        <BaseContent>
            <component
                :is="mainView"
                :show="show"
                :artists="artists"
                :popular-mixes="popularMixes"
                :latest-mixes="latestMixes"
            />
        </BaseContent>
    </Layout>
</template>

<style lang="scss" scoped>
@import '@design';

.tabs {
    @include tablet {
        padding-bottom: $s-xxl;
    }
}
</style>
